
/* eslint-disable @typescript-eslint/camelcase */
import { Options, Vue } from "vue-class-component";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { ConfigQuotaTicketDetail } from "@/domain/entities/ConfigQuotaTicket";
import { ConfigQuotaTicketController } from "@/app/ui/controllers/ConfigQuotaTicketController";
import { UserController } from "@/app/ui/controllers/UserController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import {
  ErrorMessageEntities,
  ModalMessageEntities,
  OptionsClass
} from "@/domain/entities/MainApp";
import { FlagsPermissionConfigQuotaTicket } from "@/feature-flags/flags-permission-config-quota-ticket";
import { ChipsStyles } from "@/app/ui/components/chips-v2/modules";
import { RequestListUser } from "@/data/payload/api/UserApiRequest";
import { RoleEntities, UserData } from "@/domain/entities/User";
import statusListData from "@/app/infrastructures/misc/common-library/StatusListData";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { SubData } from "@/domain/entities/MainApp";
import {
  ConfigQuotaTicketEditAccountApiRequest,
  ConfigQuotaTicketEditConfigApiRequest
} from "@/data/payload/api/ConfigQuotaTicketApiRequest";
import { QuotaTicketAccount } from "@/domain/entities/User";
import debounce from "lodash/debounce";
import { PaginationV2 } from "@/domain/entities/Pagination";
import MultipleSelectV3 from "@/app/ui/components/multiple-select-v3/index.vue";
import OverlayPanel from "primevue/overlaypanel";
import ReasonPopover from "./components/ReasonPopover.vue";
import { PodDexController } from "@/app/ui/controllers/PodDexController";
import { ReasonListDex } from "@/data/payload/api/PodDexRequest";
import { ReasonData } from "@/domain/entities/PodDex";
import { OperationalAreaController } from "@/app/ui/controllers/OperationalAreaController";
import { LocationController } from "@/app/ui/controllers/LocationController";

@Options({
  components: { MultipleSelectV3, OverlayPanel, ReasonPopover },
  beforeRouteLeave(to: any, _: any, next: any) {
    if (this.isEdit) {
      if (MainAppController.messageModal || to.name === "login") {
        MainAppController.closeMessageModal();
        next();
        this.fetchDetail();
        this.reset();
      } else {
        this.leavePageConfirmation = true;
        this.nextPath = to.path;
        next(false);
      }
    } else {
      next();
      this.fetchDetail();
    }
  }
})
export default class Form extends Vue {
  leavePageConfirmation = false;
  nextPath = "";
  onLeavePage() {
    MainAppController.setOpenMessageModal(true);
    this.$router.push(this.nextPath);
  }

  async mounted() {
    this.fetchDetail();
    // Fetch DEX reasons on component mount (used for both DEX and dispute reasons)
    await PodDexController.getReasonList(
      new ReasonListDex({
        statusCode: "DEX",
        reasonStatus: "active"
      })
    );
    // Fetch 3LC codes from API
    await this.fetchThreeLcCodes({ search: "", firstFetch: true });
  }

  reset() {
    this.userRequest = {
      pagination: new PaginationV2(),
      search: "",
      status: new OptionsClass(),
      role: new OptionsClass()
    };
  }

  get id(): string {
    return String(this.$route.params?.id);
  }
  get isEdit(): boolean {
    return !!new RegExp(/edit/).exec(this.$route.path);
  }

  detailEntities = new ResponsePayloadV2();
  get detailData(): ConfigQuotaTicketDetail {
    return this.detailEntities.data;
  }
  async fetchDetail(): Promise<void> {
    try {
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      this.detailEntities.loading = true;
      this.detailEntities = await ConfigQuotaTicketController.getDetail(
        this.id
      );
      await this.fetchRoleList();
      await this.fetchUserList();
    } catch (error) {
      const errorEntities: ErrorMessageEntities = parsingErrorResponse(error);
      this.detailEntities.message = errorEntities.type;
    } finally {
      this.detailEntities.loading = false;
    }
  }

  async fetchRoleList(): Promise<void> {
    UserController.getRole({ account_type: "customer-service" });
  }
  get roleData(): OptionsClass[] {
    return UserController.roleData.map(
      (item: RoleEntities) =>
        new OptionsClass({
          name: item.name,
          value: String(item.id)
        })
    );
  }

  userRequest = {
    pagination: new PaginationV2(),
    search: "",
    status: new OptionsClass(),
    role: new OptionsClass()
  };
  async fetchUserList(): Promise<void> {
    try {
      UserController.setLoadingList(true);
      const res = await UserController.getUserList(
        new RequestListUser({
          version: "2",
          page: this.userRequest.pagination.page,
          limit: 20,
          isTotalData: true,
          account_type: "customer-service",
          search: this.userRequest.search,
          status: this.userRequest.status.value,
          roleId: this.userRequest.role.value
        })
      );
      this.userRequest.pagination = new PaginationV2({
        page: res.pagination.page,
        totalData: res.pagination.totalData
      });
      const data = res.data.map((item: UserData) => ({
        ...item,
        isError: false
      }));
      this.detailData.userList = data;

      this.indexEditAccount = null;
      this.disabledEditAccount = false;
    } finally {
      UserController.setLoadingList(false);
    }
  }
  get userData() {
    return {
      loading: UserController.isLoading,
      isError: UserController.isError,
      message: UserController.errorCause
    };
  }

  // Helper to create simple text column
  private createTextColumn(params: {
    name: string;
    styleHead: string;
    getValue: (item: UserData) => string | number;
  }): any {
    return {
      name: params.name,
      styleHead: params.styleHead,
      render: (item: UserData) =>
        `<span class='text-left text-black-lp-300 flex break-all break-words'>${params.getValue(
          item
        )}</span>`
    };
  }

  // Helper to create quota ticket column
  private createQuotaTicketColumn(): any {
    return {
      name: "Total Kuota Tiket",
      key: this.isEdit ? "input-cell" : "",
      styleHead: "w-40 text-left",
      render: (item: UserData, index: number) =>
        this.isEdit
          ? {
              modelValue: item.dexBucketTicketAccount.dbtaBucketTicket,
              placeholder: "0",
              type: "number",
              min: "0",
              max: "9999999999",
              error: this.detailData.userList[index].isError,
              errorIcon: false,
              isErrorIconCaption: false,
              onChange: (value: string) =>
                this.onChangeEditAccount({ value, item, index }),
              "onUpdate:isError": (val: boolean) =>
                (this.detailData.userList[index].isError = val),
              disabled:
                this.indexEditAccount && Number(this.indexEditAccount) !== index
            }
          : `<span class='text-left text-black-lp-300 flex break-all break-words'>${item.dexBucketTicketAccount.dbtaBucketTicket}</span>`
    };
  }

  // Helper to create reason column with multiple-select
  private createReasonColumn(params: {
    name: string;
    slotName: string;
    key: string;
    getReasonCodes: (item: UserData) => string[];
    options: any[];
    placeholder: string;
    customSelectedItemText: string;
    onChange: (value: string[], item: UserData, index: number) => void;
    useInfiniteScroll?: boolean;
    onScrolled?: (search: string) => void;
    onFilter?: (search: string) => void;
  }): any {
    return {
      name: params.name,
      key: this.isEdit ? "multiple-select-cell" : params.key,
      useMultipleSelectV3: this.isEdit,
      "custom-slot": !this.isEdit,
      slotName: params.slotName,
      styleHead: "w-48 text-left",
      render: (item: UserData, index: number) => {
        if (!this.isEdit) {
          return "";
        }
        const reasonCodes = params.getReasonCodes(item);
        const reasonObjects = this.convertReasonCodesToObjects(
          reasonCodes,
          params.options
        );
        return this.createMultipleSelectConfig({
          reasonObjects,
          options: params.options,
          placeholder: params.placeholder,
          customSelectedItemText: params.customSelectedItemText,
          onChange: (value: any[]) => {
            const codes = this.extractCodesFromValue(value);
            params.onChange(codes, item, index);
          },
          useInfiniteScroll: params.useInfiniteScroll,
          onScrolled: params.onScrolled,
          onFilter: params.onFilter
        });
      }
    };
  }

  // Helper to create DEX reason column
  private createDexReasonColumn(): any {
    return this.createReasonColumn({
      name: "Alasan Tiket DEX",
      slotName: "alasan-tiket-dex",
      key: "reason-popover",
      getReasonCodes: (item) => this.getDexReasonCodes(item),
      options: this.dexReasons,
      placeholder: "Pilih Alasan",
      customSelectedItemText: "Alasan",
      onChange: (codes, item, index) =>
        this.onChangeDexReason({ value: codes, item, index })
    });
  }

  // Helper to create dispute reason column
  private createDisputeReasonColumn(): any {
    return this.createReasonColumn({
      name: "Alasan Tiket Sanggah",
      slotName: "alasan-tiket-sanggah",
      key: "",
      getReasonCodes: (item) => this.getDisputeReasonCodes(item),
      options: this.disputeReasons,
      placeholder: "Pilih Alasan",
      customSelectedItemText: "Alasan",
      onChange: (codes, item, index) =>
        this.onChangeDisputeReason({ value: codes, item, index })
    });
  }

  // Helper to create 3LC column
  private createThreeLcColumn(): any {
    return this.createReasonColumn({
      name: "3LC Ditugaskan",
      slotName: "3lc-dex",
      key: "",
      getReasonCodes: (item) => item.dexBucketTicketAccount.dbtaThreeLc || [],
      options: this.threeLcCodesOptions,
      placeholder: "Pilih 3LC",
      customSelectedItemText: "3LC",
      onChange: (codes, item, index) =>
        this.onChangeThreeLc({ value: codes, item, index }),
      useInfiniteScroll: true,
      onScrolled: (search: string) => this.fetchThreeLcCodesOnScroll(search),
      onFilter: (search: string) => this.searchThreeLcCodesDebounced(search)
    });
  }

  // Helper to create status column
  private createStatusColumn(): any {
    return {
      name: "Status Akun",
      styleHead: "w-40 text-left text-black-lp-300 break-all break-words",
      render: (item: UserData) => ChipsStyles({ status: item.status })
    };
  }

  // Helper to build basic text columns
  private buildBasicColumns(): any[] {
    return [
      this.createTextColumn({
        name: "Akun ID",
        styleHead: "w-32 text-left",
        getValue: (item) => item.account_id
      }),
      this.createTextColumn({
        name: "Username",
        styleHead: "w-40 text-left",
        getValue: (item) => item.username
      }),
      this.createTextColumn({
        name: "Role",
        styleHead: "w-40 text-left",
        getValue: (item) => item.account_role
      })
    ];
  }

  // Helper to build reason columns
  private buildReasonColumns(): any[] {
    return [
      this.createDexReasonColumn(),
      this.createDisputeReasonColumn(),
      this.createThreeLcColumn()
    ];
  }

  get columns(): any[] {
    return [
      ...this.buildBasicColumns(),
      this.createQuotaTicketColumn(),
      ...this.buildReasonColumns(),
      this.createStatusColumn()
    ];
  }

  onSearch(value: string) {
    this.userRequest.search = value;
    this.fetchUserList();
  }

  get statusData(): OptionsClass[] {
    return statusListData(["active", "inactive"]);
  }
  onFilter(): void {
    this.userRequest.pagination.page = 1;
    this.fetchUserList();
  }

  get isEmpty(): boolean {
    return (
      !this.detailData?.userList?.length &&
      !this.userRequest.search &&
      !this.userRequest.status.value &&
      !this.userRequest.role.value
    );
  }

  onClickToggleStatus(value: boolean) {
    this.detailData.dbtcStatus = value ? "active" : "inactive";
  }

  get subData(): SubData {
    return new SubData({
      status: this.detailData.dbtcStatus,
      isEditable: !this.isEdit && this.isEditable,
      createdAt: this.detailData.dbtcCreatedAt,
      createdBy: this.detailData.dbtcCreatedBy,
      updatedAt: this.detailData.dbtcUpdatedAt,
      updatedBy: this.detailData.dbtcUpdatedBy,
      goEdit: this.goEdit
    });
  }

  goBack(): void {
    if (!this.isEdit) {
      this.$router.push("/customer-service/config-quota-ticket/");
    } else {
      this.$router.push(`/customer-service/config-quota-ticket/${this.id}`);
    }
  }
  goEdit(): void {
    this.$router.push(`/customer-service/config-quota-ticket/${this.id}/edit`);
    this.reset();
  }

  get isEditable(): boolean {
    return FlagsPermissionConfigQuotaTicket.permission_config_quota_ticket_enable.isEnabled();
  }

  get isDisableSave(): boolean {
    return !this.detailData.dbtcDescription;
  }
  async onSave() {
    MainAppController.closeMessageModal();
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();
    try {
      await ConfigQuotaTicketController.editConfig(
        new ConfigQuotaTicketEditConfigApiRequest({
          payload: new ConfigQuotaTicketDetail({
            dbtcId: this.detailData.dbtcId,
            dbtcName: this.detailData.dbtcName,
            dbtcDescription: this.detailData.dbtcDescription,
            dbtcStatus: this.detailData.dbtcStatus,
            dbtcCreatedBy: undefined,
            dbtcUpdatedBy: undefined
          })
        })
      );
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "badge-success",
          title: "Perubahan Konfigurasi Kuota Tiket Berhasil!",
          textSuccess: "OK",
          onSubmit: this.goBack,
          imageWidth: "32",
          imageHeight: "32"
        })
      );
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal Mengubah Konfigurasi Kuota Tiket!",
          this.onSave
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  disabledEditAccount = false;
  indexEditAccount: string | null = null;

  // DEX reasons from PodDexController with "Semua" option
  get dexReasons() {
    const reasons = PodDexController.reasonList.map((reason: ReasonData) => ({
      name: reason.reasonDescription,
      value: reason.reasonCode
    }));
    // Add "Semua" option at the beginning
    return [{ name: "Semua", value: "ALL" }, ...reasons];
  }

  // Dispute reasons from PodDexController (using DEX statusCode) with "Semua" option
  get disputeReasons() {
    const reasons = PodDexController.reasonList.map((reason: ReasonData) => ({
      name: reason.reasonDescription,
      value: reason.reasonCode
    }));
    // Add "Semua" option at the beginning
    return [{ name: "Semua", value: "ALL" }, ...reasons];
  }

  // 3LC codes from API
  threeLcCodes: Array<{ name: string; value: string }> = [];
  threeLcCurrentPage = 1;
  threeLcLimit = 1000;
  threeLcHasMore = true;

  async fetchThreeLcCodes({ search = "", firstFetch = true }): Promise<void> {
    try {
      if (firstFetch) {
        this.threeLcCodes = [];
        this.threeLcCurrentPage = 1;
        this.threeLcHasMore = true;
      }

      const response = await LocationController.getCityList({
        search: search,
        status: "active",
        page: this.threeLcCurrentPage,
        limit: this.threeLcLimit,
        newEndpoint: true
      });

      const cityData = response.cityData || [];

      // Stop infinite scroll if data is empty
      if (cityData.length === 0) {
        this.threeLcHasMore = false;
        return;
      }

      // Map city codes to options format (no sorting)
      const newCityCodes = cityData
        .filter((city) => city.code && city.code.trim())
        .map((city) => ({
          name: `${city.code} - ${city.name}`,
          value: city.code
        }));

      // Push to existing array
      this.threeLcCodes.push(...newCityCodes);

      // Check if we got less data than requested (last page)
      if (cityData.length < this.threeLcLimit) {
        this.threeLcHasMore = false;
      }

    } catch (error) {
      console.error("Error fetching 3LC codes:", error);
      if (firstFetch) {
        this.threeLcCodes = [];
        this.threeLcCurrentPage = 1;
      }
      this.threeLcHasMore = false;
    }
  }

  async fetchThreeLcCodesOnScroll(search = ""): Promise<void> {
    // Don't fetch if no more data available
    if (!this.threeLcHasMore) return;

    this.threeLcCurrentPage++;
    await this.fetchThreeLcCodes({ search, firstFetch: false });
  }

  async searchThreeLcCodes(search = ""): Promise<void> {
    try {
      if (!search || search.trim().length === 0) {
        // Don't refetch if we already have data
        // Just return to show existing data
        return;
      }

      const response = await LocationController.getCityList({
        search: search,
        status: "active",
        page: 1,
        limit: 50,
        newEndpoint: true
      });

      const cityData = response.cityData || [];

      // Map search results to options format
      const searchResults = cityData
        .filter((city) => city.code && city.code.trim())
        .map((city) => ({
          name: `${city.code} - ${city.name}`,
          value: city.code
        }));

      // Get existing values to avoid duplicates
      const existingValues = new Set(this.threeLcCodes.map(item => item.value));

      // Append only new items that don't exist yet
      const newItems = searchResults.filter(item => !existingValues.has(item.value));

      if (newItems.length > 0) {
        this.threeLcCodes.push(...newItems);
      }

    } catch (error) {
      console.error("Error searching 3LC codes:", error);
    }
  }

  searchThreeLcCodesDebounced = debounce((search: string) => {
    this.searchThreeLcCodes(search);
  }, 500);

  get threeLcCodesOptions(): Array<{ name: string; value: string }> {
    // Add "Semua" option at the beginning
    return [{ name: "Semua", value: "ALL" }, ...this.threeLcCodes];
  }

  // Helper to get all reason names for tooltip
  getAllReasonNames(selectedIds: string[], options: any[]): string[] {
    if (!selectedIds || selectedIds.length === 0) {
      return [];
    }
    // Handle "ALL" case
    if (selectedIds.length === 1 && selectedIds[0] === "ALL") {
      const allOption = options.find((opt) => opt.value === "ALL");
      return [allOption ? allOption.name : "Semua"];
    }
    return selectedIds.map((id) => {
      const option = options.find((opt) => opt.value === id);
      return option ? option.name : id;
    });
  }

  onChangeEditAccount(params: {
    value: string;
    item: UserData;
    index: number;
  }) {
    this.detailData.userList[
      params.index
    ].dexBucketTicketAccount.dbtaBucketTicket = params.value;
    this.indexEditAccount = String(params.index);
    this.disabledEditAccount = true;
    this.onEditAccount(params.item);
  }
  onEditAccount = debounce(async (item: UserData) => {
    const value = item.dexBucketTicketAccount.dbtaBucketTicket;
    if (value !== null && value !== undefined && value !== "") {
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();
      try {
        // Prepare payload with existing reason codes and 3LC codes to preserve them
        const dexReasonCodes =
          item.dexBucketTicketAccount?.dbtaDexReasonCode || [];
        const disputeReasonCodes =
          item.dexBucketTicketAccount?.dbtaDisputeDexReasonCode || [];
        const threeLcCodes = item.dexBucketTicketAccount?.dbtaThreeLc || [];

        await ConfigQuotaTicketController.editAccount(
          new ConfigQuotaTicketEditAccountApiRequest({
            accountId: item.account_id,
            payload: new QuotaTicketAccount({
              dbtaBucketTicket: Number(
                item.dexBucketTicketAccount.dbtaBucketTicket
              ),
              // Preserve existing reason codes and 3LC codes
              dexReasonCodes: dexReasonCodes.length > 0 ? dexReasonCodes : [],
              disputeReasonCodes:
                disputeReasonCodes.length > 0 ? disputeReasonCodes : [],
              dbtaThreeLc: threeLcCodes.length > 0 ? threeLcCodes : []
            })
          })
        );
        MainAppController.setSnackbarMessage(`
          <div class='flex flex-col'>
            <div class='text-16px text-green-lp-400 font-bold'>Berhasil menyimpan</div>
            <p class='text-12px text-green-lp-400'>Perubahan total Kuota Tiket Berhasil</p>
          </div>
        `);
        MainAppController.setSnackbarPositivity(true);
        MainAppController.setSnackbarTimeout(3000);
        MainAppController.setSnackbarBackgroundColor("#E3FCEF");
        MainAppController.setSnackbarIconLeft("success-status");
        MainAppController.setSnackbar(true);

        this.indexEditAccount = null;
        this.disabledEditAccount = false;
      } catch (error) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            error,
            "Gagal Mengubah total Kuota Tiket!",
            this.onSave
          )
        );
      } finally {
        MainAppController.closeLoading();
      }
    } else {
      this.disabledEditAccount = false;
    }
  }, 3000);

  // Helper to check if reason codes is empty or null
  private isEmptyReasonCodes(
    reasonCodes: string[] | null | undefined
  ): boolean {
    return (
      reasonCodes === null ||
      reasonCodes === undefined ||
      reasonCodes.length === 0
    );
  }

  // Helper to check if reason codes is "ALL"
  private isAllReasonCode(reasonCodes: string[]): boolean {
    return reasonCodes.length === 1 && reasonCodes[0] === "ALL";
  }

  // Helper to get reason codes from item (returns array of strings for API)
  private getReasonCodesFromItem(
    reasonCodes: string[] | null | undefined
  ): string[] {
    if (this.isEmptyReasonCodes(reasonCodes)) {
      return [];
    }
    // If it's ["ALL"], return ["ALL"] for display
    if (this.isAllReasonCode(reasonCodes!)) {
      return ["ALL"];
    }
    // Return specific reason codes
    return reasonCodes!;
  }

  // Helper to get DEX reason codes from item (returns array of strings for API)
  getDexReasonCodes(item: UserData): string[] {
    return this.getReasonCodesFromItem(
      item.dexBucketTicketAccount?.dbtaDexReasonCode
    );
  }

  // Helper to get Dispute reason codes from item (returns array of strings for API)
  getDisputeReasonCodes(item: UserData): string[] {
    return this.getReasonCodesFromItem(
      item.dexBucketTicketAccount?.dbtaDisputeDexReasonCode
    );
  }

  // Helper to convert reason codes to objects for MultipleSelectV3 display
  // Returns array of objects with {name, value} format for display
  convertReasonCodesToObjects(reasonCodes: string[], options: any[]): any[] {
    if (!reasonCodes || reasonCodes.length === 0) {
      return [];
    }
    return reasonCodes
      .map((code) => {
        const option = options.find((opt) => opt.value === code);
        if (option) {
          return { name: option.name, value: option.value };
        }
        // Fallback: if code not found, return object with code as both name and value
        return { name: code, value: code };
      })
      .filter((item) => item !== null) as any[];
  }

  // Helper to create multiple-select config for reason codes
  private createMultipleSelectConfig(params: {
    reasonObjects: any[];
    options: any[];
    placeholder: string;
    customSelectedItemText: string;
    onChange: (value: any[]) => void;
    useInfiniteScroll?: boolean;
    onScrolled?: (search: string) => void;
    onFilter?: (search: string) => void;
  }): any {
    return {
      modelValue: params.reasonObjects,
      options: params.options,
      placeholder: params.placeholder,
      keyName: "name",
      keyValue: "value",
      displayValue: false,
      showSelected: false,
      showSelectedInInput: true,
      customSelectedItem: true,
      customSelectedItemText: params.customSelectedItemText,
      onChange: params.onChange,
      useInfiniteScroll: params.useInfiniteScroll || false,
      onScrolled: params.onScrolled,
      onFilter: params.onFilter
    };
  }

  // Helper to extract codes from multiple-select value
  private extractCodesFromValue(value: any[]): string[] {
    return value.map((item) => (typeof item === "string" ? item : item.value));
  }

  onChangeDexReason(params: {
    value: string[];
    item: UserData;
    index: number;
  }) {
    const reasonCodes = this.normalizeReasonCodes(params.value);
    this.detailData.userList[
      params.index
    ].dexBucketTicketAccount.dbtaDexReasonCode = reasonCodes;
    this.onEditAccountWithReasons(params.item);
  }

  onChangeDisputeReason(params: {
    value: string[];
    item: UserData;
    index: number;
  }) {
    const reasonCodes = this.normalizeReasonCodes(params.value);
    this.detailData.userList[
      params.index
    ].dexBucketTicketAccount.dbtaDisputeDexReasonCode = reasonCodes;
    this.onEditAccountWithReasons(params.item);
  }

  // Normalize reason codes: if "ALL" is selected, return ["ALL"], otherwise return specific codes
  normalizeReasonCodes(codes: string[]): string[] | null {
    if (!codes || codes.length === 0) {
      return null; // Empty array means null (not assigned)
    }
    // Check if "ALL" is in the selection
    if (codes.includes("ALL")) {
      return ["ALL"];
    }
    // Return specific reason codes
    return codes;
  }

  onChangeThreeLc(params: { value: string[]; item: UserData; index: number }) {
    // Save original value before validation
    const originalThreeLc =
      this.detailData.userList[params.index].dexBucketTicketAccount
        .dbtaThreeLc || [];

    const threeLcCodes = this.normalizeReasonCodes(params.value);
    const threeLcArray = threeLcCodes ? threeLcCodes : [];

    // Validate: 3LC is mandatory, cannot be empty
    if (threeLcArray.length === 0) {
      // Restore original value to prevent visual "0 3LC"
      // Create new array reference and new object reference to trigger reactivity
      const restoredValue =
        originalThreeLc.length > 0 ? [...originalThreeLc] : [];

      // Create new object references to ensure Vue detects the change
      const updatedUser = {
        ...this.detailData.userList[params.index],
        dexBucketTicketAccount: {
          ...this.detailData.userList[params.index].dexBucketTicketAccount,
          dbtaThreeLc: restoredValue
        }
      };

      // Update the userList with new reference
      this.detailData.userList = [
        ...this.detailData.userList.slice(0, params.index),
        updatedUser,
        ...this.detailData.userList.slice(params.index + 1)
      ];

      MainAppController.setSnackbarMessage(`
        <div class='flex flex-col'>
          <div class='text-16px text-red-lp-1200 font-bold'>Gagal menyimpan</div>
          <p class='text-12px text-red-lp-1200'>3LC Ditugaskan wajib diisi</p>
        </div>
      `);
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarBackgroundColor("#FEE8E8");
      MainAppController.setSnackbarIconLeft("icon-check-circle-red");
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbar(true);
      return;
    }

    this.detailData.userList[
      params.index
    ].dexBucketTicketAccount.dbtaThreeLc = threeLcArray;
    this.onEditAccountWithReasons(params.item);
  }

  onEditAccountWithReasons = debounce(async (item: UserData) => {
    MainAppController.closeMessageModal();
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();
    try {
      // Prepare payload with new API field names
      const dexReasonCodes =
        item.dexBucketTicketAccount?.dbtaDexReasonCode || [];
      const disputeReasonCodes =
        item.dexBucketTicketAccount?.dbtaDisputeDexReasonCode || [];
      const threeLcCodes = item.dexBucketTicketAccount?.dbtaThreeLc || [];

      await ConfigQuotaTicketController.editAccount(
        new ConfigQuotaTicketEditAccountApiRequest({
          accountId: item.account_id,
          payload: new QuotaTicketAccount({
            dbtaBucketTicket: Number(
              item.dexBucketTicketAccount.dbtaBucketTicket
            ),
            // Send empty array if null or empty, otherwise send the array
            dexReasonCodes: dexReasonCodes.length > 0 ? dexReasonCodes : [],
            disputeReasonCodes:
              disputeReasonCodes.length > 0 ? disputeReasonCodes : [],
            dbtaThreeLc: threeLcCodes.length > 0 ? threeLcCodes : []
          })
        })
      );
      MainAppController.setSnackbarMessage(`
        <div class='flex flex-col'>
          <div class='text-16px text-green-lp-400 font-bold'>Berhasil menyimpan</div>
          <p class='text-12px text-green-lp-400'>Perubahan Konfigurasi Berhasil</p>
        </div>
      `);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(3000);
      MainAppController.setSnackbarBackgroundColor("#E3FCEF");
      MainAppController.setSnackbarIconLeft("success-status");
      MainAppController.setSnackbar(true);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Gagal Mengubah Konfigurasi!", () =>
          this.onEditAccountWithReasons(item)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }, 3000);
}
